import type {
  PromisedReward,
  RewardPromise,
  Tournament,
  TournamentDetails,
  TournamentParticipant,
  TournamentSummary,
} from "@/shared/api/types/tournaments"

/** Finite number or null. */
function finiteNumber(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null
}

function optionalString(v: unknown): string | null {
  return typeof v === "string" ? v : null
}

function parsePromisedReward(raw: unknown): PromisedReward {
  if (!raw || typeof raw !== "object") {
    return { tonNano: 0, giftNftIds: [] }
  }
  const o = raw as Record<string, unknown>
  const ton = finiteNumber(o.ton_amount)
  const ids = Array.isArray(o.gift_nft_ids)
    ? o.gift_nft_ids.filter((x): x is string => typeof x === "string")
    : []
  return { tonNano: ton ?? 0, giftNftIds: ids }
}

function parseRewardPromise(raw: unknown): RewardPromise | null {
  if (!raw || typeof raw !== "object") return null
  const o = raw as Record<string, unknown>
  const place = finiteNumber(o.place)
  const bps = finiteNumber(o.ton_pool_bps)
  if (place === null || bps === null) return null
  const ids = Array.isArray(o.gift_nft_ids)
    ? o.gift_nft_ids.filter((x): x is string => typeof x === "string")
    : []
  return { place, tonPoolBps: bps, giftNftIds: ids }
}

function parseSummaryBase(o: Record<string, unknown>): TournamentSummary | null {
  const id = o.id
  if (typeof id !== "string" || id.length === 0) return null

  const status = o.status
  if (
    status !== "UPCOMING" &&
    status !== "ACTIVE" &&
    status !== "FINISHED"
  ) {
    return null
  }

  const name = o.name
  if (typeof name !== "string") return null

  const startsAt = finiteNumber(o.starts_at)
  const endsAt = finiteNumber(o.ends_at)
  const poolFundingBps = finiteNumber(o.pool_funding_bps)
  const prizePlacesCount = finiteNumber(o.prize_places_count)
  const currentPool = finiteNumber(o.current_pool_ton_equiv)
  if (
    startsAt === null ||
    endsAt === null ||
    poolFundingBps === null ||
    prizePlacesCount === null ||
    currentPool === null
  ) {
    return null
  }

  const finalizedAt = finiteNumber(o.finalized_at)

  return {
    id,
    status,
    name,
    startsAt,
    endsAt,
    poolFundingBps,
    prizePlacesCount,
    currentPoolNanoTon: currentPool,
    finalizedAt,
    isUpcoming: status === "UPCOMING",
    isFinished: status === "FINISHED" || finalizedAt !== null,
  }
}

/** Bootstrap summary item (no reward_promises). */
export function parseTournamentSummary(raw: unknown): TournamentSummary | null {
  if (!raw || typeof raw !== "object") return null
  return parseSummaryBase(raw as Record<string, unknown>)
}

/** Full tournament (details + realtime); summary + reward_promises. */
export function parseTournament(raw: unknown): Tournament | null {
  if (!raw || typeof raw !== "object") return null
  const o = raw as Record<string, unknown>
  const summary = parseSummaryBase(o)
  if (!summary) return null
  const promisesRaw = Array.isArray(o.reward_promises) ? o.reward_promises : []
  const rewardPromises: RewardPromise[] = []
  for (const p of promisesRaw) {
    const parsed = parseRewardPromise(p)
    if (parsed) rewardPromises.push(parsed)
  }
  return { ...summary, rewardPromises }
}

export function parseTournamentParticipant(
  raw: unknown
): TournamentParticipant | null {
  if (!raw || typeof raw !== "object") return null
  const p = raw as Record<string, unknown>

  const userId = p.user_id
  if (typeof userId !== "string" || userId.length === 0) return null

  const totalWon = finiteNumber(p.total_won_ton_equiv)
  const totalLost = finiteNumber(p.total_lost_ton_equiv)
  const netScore = finiteNumber(p.net_score_ton_equiv)
  const volume = finiteNumber(p.participation_volume_ton_equiv)
  const rounds = finiteNumber(p.rounds_count)
  const rank = finiteNumber(p.rank)
  if (
    totalWon === null ||
    totalLost === null ||
    netScore === null ||
    volume === null ||
    rounds === null ||
    rank === null
  ) {
    return null
  }

  return {
    userId,
    username: optionalString(p.username),
    photoUrl: optionalString(p.photo_url),
    totalWonNanoTon: totalWon,
    totalLostNanoTon: totalLost,
    netScoreNanoTon: netScore,
    participationVolumeNanoTon: volume,
    roundsCount: rounds,
    rank,
    promisedReward: parsePromisedReward(p.promised_reward),
  }
}

/** `GET /v1/games/tournaments/{id}` response. */
export function parseTournamentDetails(
  raw: unknown
): { data: TournamentDetails; error: null } | { data: null; error: string } {
  if (!raw || typeof raw !== "object") {
    return { data: null, error: "Root value must be a JSON object" }
  }
  const o = raw as Record<string, unknown>

  const tournament = parseTournament(o.tournament)
  if (!tournament) {
    return { data: null, error: "`tournament` is missing or malformed" }
  }

  const lb = o.leaderboard
  if (!lb || typeof lb !== "object") {
    return { data: null, error: "`leaderboard` must be an object" }
  }
  const lbObj = lb as Record<string, unknown>
  const itemsRaw = Array.isArray(lbObj.items) ? lbObj.items : []
  const items: TournamentParticipant[] = []
  for (const it of itemsRaw) {
    const parsed = parseTournamentParticipant(it)
    if (parsed) items.push(parsed)
  }
  const totalCount = finiteNumber(lbObj.total_count) ?? items.length

  const myStanding =
    o.my_standing == null ? null : parseTournamentParticipant(o.my_standing)

  return {
    data: { tournament, leaderboard: items, myStanding, totalCount },
    error: null,
  }
}

/**
 * Realtime event payload — `tournament` envelope (no `my_standing`). Returns
 * the tournament + top-10 leaderboard; callers merge into the store.
 */
export function parseRealtimeTournamentState(
  raw: unknown
): {
  data: { tournament: Tournament; leaderboard: TournamentParticipant[] }
  error: null
} | { data: null; error: string } {
  if (!raw || typeof raw !== "object") {
    return { data: null, error: "Root value must be a JSON object" }
  }
  const o = raw as Record<string, unknown>

  const tournament = parseTournament(o.tournament)
  if (!tournament) {
    return { data: null, error: "`tournament` is missing or malformed" }
  }

  const lbRaw = Array.isArray(o.leaderboard) ? o.leaderboard : []
  const leaderboard: TournamentParticipant[] = []
  for (const it of lbRaw) {
    const parsed = parseTournamentParticipant(it)
    if (parsed) leaderboard.push(parsed)
  }

  return { data: { tournament, leaderboard }, error: null }
}
